import { useContext, useState, useMemo } from "react";
import { Routes, Route } from "react-router-dom";
import { StyledDataContainer } from "../../Provider.styles";
import { ButtonArray } from "../../Provider";
import { useNavigate } from "react-router-dom";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import { GlobalContext } from "context/Provider";
import Edit from "./Edit";
import Upsert from "../../Upsert/Upsert";
import API from "api";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import GlobalStyles from "@mui/material/GlobalStyles";
import Button from "components/shared/Button/Button";

interface ListProps {
  columns: GridColDef[];
  rows: any[] | undefined;
  action: (event: any) => void;
  loading: boolean;
}

interface UsersProps {
  loadingCoords: boolean;
  loadingMems: boolean;
  loadingArch: boolean;
}

const List = (props: ListProps) => {
  const { columns, rows, action, loading } = props;
  const [memberFilter, setMemberFilter] = useState<"all" | "locked" | "active">("all");

  const filteredRows = useMemo(() => {
    if (!rows) return [];

    switch (memberFilter) {
      case "locked":
        return rows.filter((row) => row?.locked === true);
      case "active":
        return rows.filter((row) => row?.enrollmentStatus !== "paused" && row?.enrollmentStatus !== "pending");
      case "all":
      default:
        return rows;
    }
  }, [rows, memberFilter]);

  return (
    <div>
      <ButtonArray
        memberFilter={memberFilter}
        setMemberFilter={setMemberFilter}
      />
      <StyledDataContainer>
        <GlobalStyles
          styles={{ ".MuiDataGrid-panel": { top: "-160px !important" } }}
        />
        <DataGridPro
          columns={columns}
          slots={{
            toolbar: () => (
              <GridToolbarContainer style={{ justifyContent: "flex-start" }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
              </GridToolbarContainer>
            )
          }}
          rows={filteredRows || []}
          disableRowSelectionOnClick
          pageSizeOptions={[5]}
          onCellClick={action}
          disableColumnPinning
        />
      </StyledDataContainer>
    </div>
  );
};

export default function Users({
  loadingCoords,
  loadingMems,
  loadingArch
}: UsersProps)  {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { provider, memberDispatch } = useContext(GlobalContext);

  const removeUser = (id: string, isCoordinator: boolean) => {
    if (window.confirm("Are you sure you want to archive this user?")) {
      const apiCall = isCoordinator
        ? API.kore.coordinator.remove(id, Session.crackCookieToken("provider"))
        : API.kore.members.remove(id, Session.crackCookieToken("provider"));

      apiCall
        .then(() => {
          window.location.pathname = `/provider/${isCoordinator ? "coordinator" : "member"}s`;
        })
        .catch((err) => {
          console.error("Failed to archive user");
        });
    }
  };
  const restoreUser = (id: string) => {
    API.kore.archived.unarchiveById(id, Session.crackCookieToken("provider"))
      .then(() => {
        window.location.pathname = "/provider/archived";
      })
      .catch((err) => {
        console.error("Failed to restore user");
      });
  };

  const coordinatorHeaders: GridColDef[] = [
    { field: "name", headerName: t("u-name"), width: 125, flex: 500 },
    { field: "email", headerName: t("u-email"), width: 125, flex: 500 },
    {
      field: "actions",
      headerName: t("u-actions"),
      width: 140,
      flex: 150,
      renderCell: (params) => (
        <Button
          variant="primary"
          onClick={(event) => {
            event.stopPropagation();
            removeUser(params.row.id, true);
          }}
        >
          {t("u-archive")}
        </Button>
      )
    }
  ];
  const memberHeaders: GridColDef[] = [
    {
      field: "name",
      headerName: t("u-name"),
      editable: true,
      width: 125,
      flex: 500
    },
    {
      field: "email",
      headerName: t("u-email"),
      editable: true,
      width: 125,
      flex: 500
    },
    {
      field: "phone",
      headerName: t("u-phone"),
      editable: true,
      width: 100,
      flex: 500
    },
    {
      field: "lastactivity",
      headerName: t("u-last-activity"),
      editable: true,
      width: 100,
      flex: 500
    },
    {
      field: "registeredat",
      headerName: t("u-registered-at"),
      editable: true,
      width: 100,
      flex: 500
    },
    {
      field: "actions",
      headerName: t("u-actions"),
      width: 140,
      flex: 500,
      renderCell: (params) => (
        <Button
          variant="primary"
          onClick={(event) => {
            event.stopPropagation();
            removeUser(params.row.id, false);
          }}
        >
          {t("u-archive")}
        </Button>
      )
    }
  ];
  const archiveHeaders: GridColDef[] = [
    {
			field: "fullName",
			headerName: t("u-name"),
			description: "This column represents the name of the archived member.",
      width: 140,
      flex: 500,
			align: "left",
		},
		{
			field: "archivedAt",
			headerName: "Archived At",
			// headerName: t("u-archived-at"),
			description:
				"This column represents the date and time the member was archived.",
			width: 140,
      flex: 500,
			align: "left",
		},
		{
			field: "entity",
			headerName: "Entity",
			// headerName: t("u-entity"),
			description:
				"This column represents whether a member is a patient or coordinator.",
			width: 140,
      flex: 300,
			align: "left",
		},
		{
			field: "username",
			headerName: "Username",
			// headerName: t("u-username"),
			description: "This column represents the username of the member.",
			width: 140,
      flex: 500,
			align: "left",
		},
		{
			field: "externId",
			headerName: "MRN",
			// headerName: t("u-mrn"),
			description: "This column represents the MRN of the member.",
			width: 140,
      flex: 500,
			align: "left",
		},
		{
			field: "actions",
			headerName: "Restore",
			description: "This button will allow you to unarchive a member.",
			width: 140,
      flex: 200,
			align: "left",
      renderCell: (params) => (
        <Button
          variant="primary"
          onClick={(event) => {
            event.stopPropagation();
            restoreUser(params.row.id);
          }}
        >
          Restore
        </Button>
      )
		},
  ];
  const editUser = (isCoordinator: boolean) => (event: any) => {
    const { id } = event.row;
    let userCall = API.kore.members.getOne(
      id,
      Session.crackCookieToken("provider")
    );
    if (isCoordinator) {
      userCall = API.kore.coordinator.getSingle(
        id,
        Session.crackCookieToken("provider")
      );
    }
    userCall.then((user) => {
      memberDispatch({ type: "CHANGE_TARGET_MEMBER", payload: user });
      navigate(`${window.location.pathname}/edit`);
    });
  };
  return (
    <Routes>
      <Route
        path="coordinators"
        element={
          <List
            columns={coordinatorHeaders}
            rows={provider?.coordinatorList}
            action={editUser(true)}
            loading={loadingCoords}
          />
        }
      />
      <Route
        path="members"
        element={
          <List
            columns={memberHeaders}
            rows={provider?.memberList}
            action={editUser(false)}
            loading={loadingMems}
          />
        }
      />
      <Route
        path="archived"
        element={
          <List
            columns={archiveHeaders}
            rows={provider?.archivedList}
            action={editUser(false)}
            loading={loadingArch}
          />
        }
      />
      <Route path="coordinators/edit" element={<Edit type={"coordinator"} />} />
      <Route path="members/edit" element={<Edit type={"member"} />} />
      <Route path="archived/edit" element={<Edit type={"archived"} />} />
      <Route path="coordinators/new" element={<Upsert type="coordinator" />} />
      <Route path="members/new" element={<Upsert type="member" />} />
    </Routes>
  );
}
